import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import { mapSettingsError, SettingsApiError } from './settings-api.errors';
import { SETTINGS_API_URL } from './settings-api.tokens';
import {
  UserAccessDto,
  UserApiDownload,
  UserDto,
  UserFilterSelects,
  UserImageDto,
  UserListItem,
  UserListQuery,
  UserListResult,
  UserRoleOption,
  UserStatusDto,
  UserWriteRequest,
} from './users-api.types';

const REQUEST_TIMEOUT_MS = 15_000;
const UPLOAD_TIMEOUT_MS = 60_000;
const LIST_PAGE_SIZE = 100;
const USERS_PATH = '/users';

@Injectable({ providedIn: 'root' })
export class UsersApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(SETTINGS_API_URL).replace(/\/+$/, '')}${USERS_PATH}`;

  list(query: UserListQuery = {}): Observable<UserListResult> {
    return this.safeListRequest(
      this.http.get<unknown>(this.baseUrl, { params: listParams(query) }),
      unwrapList,
    );
  }

  filterSelects(): Promise<UserFilterSelects> {
    return this.safeRequest(
      this.http.get<unknown>(`${this.baseUrl}/filter-selects`),
      unwrapFilterSelects,
    );
  }

  accessFormSelects(): Promise<UserRoleOption[]> {
    return this.safeRequest(
      this.http.get<unknown>(`${this.baseUrl}/access/form-selects`),
      unwrapRoles,
    );
  }

  getById(uuid: string): Promise<UserDto> {
    return this.safeRequest(
      this.http.get<unknown>(`${this.baseUrl}/${uuid}/general`),
      unwrapUser,
    );
  }

  getAccess(uuid: string): Promise<UserAccessDto> {
    return this.safeRequest(
      this.http.get<unknown>(`${this.baseUrl}/${uuid}/access`),
      unwrapAccess,
    );
  }

  create(payload: UserWriteRequest, image?: File): Promise<UserDto> {
    return this.request(
      this.write('POST', this.baseUrl, payload, image),
      unwrapUser,
      image ? UPLOAD_TIMEOUT_MS : REQUEST_TIMEOUT_MS,
    );
  }

  updateGeneral(
    uuid: string,
    payload: UserWriteRequest,
    image?: File,
  ): Promise<UserDto> {
    return this.request(
      this.write('PATCH', `${this.baseUrl}/${uuid}/general`, payload, image),
      unwrapUser,
      image ? UPLOAD_TIMEOUT_MS : REQUEST_TIMEOUT_MS,
    );
  }

  activate(uuid: string): Promise<void> {
    return this.request(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/activate`, null),
      () => undefined,
    );
  }

  inactivate(uuid: string): Promise<void> {
    return this.request(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/inactivate`, null),
      () => undefined,
    );
  }

  remove(uuid: string): Promise<void> {
    return this.request(
      this.http.delete<unknown>(`${this.baseUrl}/${uuid}`),
      () => undefined,
    );
  }

  exportUsers(query: UserListQuery = {}): Promise<UserApiDownload> {
    return this.download(
      `${this.baseUrl}/excel`,
      listParams(query, false),
      'usuarios.xlsx',
    );
  }

  private write(
    method: 'POST' | 'PATCH',
    url: string,
    payload: UserWriteRequest,
    image?: File,
  ): Observable<unknown> {
    if (!image) {
      return this.http.request<unknown>(method, url, { body: payload });
    }
    return this.http.request<unknown>(method, url, {
      body: userFormData(payload, image),
    });
  }

  private safeListRequest<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Observable<T> {
    return source.pipe(
      timeout(REQUEST_TIMEOUT_MS),
      retry({ count: 1, delay: error => retryDelay(error) }),
      map(project),
      catchError(error => throwError(() => mapUsersError(error))),
    );
  }

  private safeRequest<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    return this.request(
      source.pipe(retry({ count: 1, delay: error => retryDelay(error) })),
      project,
    );
  }

  private async request<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
    timeoutMs = REQUEST_TIMEOUT_MS,
  ): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(timeoutMs)));
      return project(body);
    } catch (error) {
      throw mapUsersError(error);
    }
  }

  private async download(
    url: string,
    params: HttpParams,
    fallbackFilename: string,
  ): Promise<UserApiDownload> {
    try {
      const response = await firstValueFrom(
        this.http
          .get(url, { params, observe: 'response', responseType: 'blob' })
          .pipe(
            timeout(REQUEST_TIMEOUT_MS),
            retry({ count: 1, delay: error => retryDelay(error) }),
          ),
      );
      return toDownload(response, fallbackFilename);
    } catch (error) {
      throw mapUsersError(error);
    }
  }
}

function userFormData(payload: UserWriteRequest, image: File): FormData {
  const formData = new FormData();
  formData.append(
    'data',
    new Blob([JSON.stringify(payload)], { type: 'application/json' }),
  );
  formData.append('image', image);
  return formData;
}

function listParams(query: UserListQuery, includePagination = true): HttpParams {
  let params = new HttpParams();
  if (includePagination) {
    params = params
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? LIST_PAGE_SIZE));
  }
  if (query.firstName) params = params.set('filters[firstName]', query.firstName);
  if (query.lastName) params = params.set('filters[lastName]', query.lastName);
  if (query.secondLastName) {
    params = params.set('filters[secondLastName]', query.secondLastName);
  }
  if (query.email) params = params.set('filters[email]', query.email);
  if (query.phone) params = params.set('filters[phone]', query.phone);
  if (query.statusUuids?.length) {
    params = params.set('select[status]', query.statusUuids.join(','));
  }
  if (query.sortBy) params = params.set('sortBy', query.sortBy);
  if (query.order) params = params.set('order', query.order);
  return params;
}

function retryDelay(error: unknown): Observable<number> {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      const seconds =
        Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
      const jitterMs = Math.floor(Math.random() * 250);
      return timer(seconds * 1000 + jitterMs);
    }
    if (error.status >= 500 || error.status === 0) {
      return timer(300);
    }
  }
  return throwError(() => error);
}

function mapUsersError(error: unknown): SettingsApiError {
  const mapped = mapSettingsError(error);
  if (!(error instanceof HttpErrorResponse)) return mapped;
  const body = asRecord(error.error);
  if (!body) return mapped;
  const business = stringField(body, ['error']);
  const details = detailMessages(body['details']).filter(item => item !== business);
  if (business && details.length) {
    mapped.message = [business.replace(/\.$/, ''), ...details].join('. ');
  } else if (business) {
    mapped.message = business;
  } else if (details.length) {
    mapped.message = details.join('. ');
  }
  return mapped;
}

function unwrapList(body: unknown): UserListResult {
  const data = unwrapData(body);
  if (Array.isArray(data)) {
    return { items: data.map(normalizeListItem), total: data.length };
  }
  const page = asRecord(data);
  if (!page) throw new SettingsApiError('unexpected', 0);
  const items = firstArray(page, ['data', 'content', 'items']).map(normalizeListItem);
  const pagination = asRecord(page['pagination']);
  const total =
    numberField(pagination, ['totalRecords']) ??
    numberField(page, ['totalElements', 'total']) ??
    items.length;
  return { items, total };
}

function unwrapUser(body: unknown): UserDto {
  return normalizeUser(requiredData(body));
}

function unwrapAccess(body: unknown): UserAccessDto {
  const record = asRecord(requiredData(body));
  if (!record) throw new SettingsApiError('unexpected', 0);
  return {
    configured: record['configured'] === true,
    requiresNormalization: record['requiresNormalization'] === true,
    enabled: record['enabled'] === true,
    role: normalizeAccessRole(record['role']),
    systems: firstArray(record, ['systems'])
      .map(normalizeSystem)
      .filter((item): item is UserAccessDto['systems'][number] => item !== null),
  };
}

function unwrapFilterSelects(body: unknown): UserFilterSelects {
  const data = asRecord(unwrapData(body)) ?? asRecord(body);
  if (!data) throw new SettingsApiError('unexpected', 0);
  return {
    statuses: firstArray(data, ['statuses', 'status'])
      .map(normalizeNamed)
      .filter((item): item is UserRoleOption => item !== null),
  };
}

function unwrapRoles(body: unknown): UserRoleOption[] {
  const data = asRecord(unwrapData(body)) ?? asRecord(body);
  if (!data) throw new SettingsApiError('unexpected', 0);
  return firstArray(data, ['roles'])
    .map(normalizeRole)
    .filter((item): item is UserRoleOption => item !== null);
}

function normalizeListItem(raw: unknown): UserListItem {
  const record = asRecord(raw);
  const uuid = stringField(record, ['uuid']);
  const email = stringField(record, ['email']);
  const status = record ? normalizeStatus(record['status']) : null;
  if (!record || !uuid || !email || !status) {
    throw new SettingsApiError('unexpected', 0);
  }
  return {
    uuid,
    firstName: stringField(record, ['firstName']) ?? '',
    lastName: stringField(record, ['lastName']) ?? '',
    secondLastName: nullableString(record['secondLastName']),
    type: stringField(record, ['type']) ?? '',
    email,
    phone: stringField(record, ['phone']) ?? '',
    status,
  };
}

function normalizeUser(raw: unknown): UserDto {
  const record = asRecord(raw);
  const uuid = stringField(record, ['uuid']);
  const email = stringField(record, ['email']);
  const status = record ? normalizeStatus(record['status']) : null;
  if (!record || !uuid || !email || !status) {
    throw new SettingsApiError('unexpected', 0);
  }
  return {
    uuid,
    firstName: stringField(record, ['firstName']) ?? '',
    lastName: stringField(record, ['lastName']) ?? '',
    secondLastName: nullableString(record['secondLastName']),
    type: stringField(record, ['type']) ?? '',
    email,
    phoneCountryCode: nullableString(record['phoneCountryCode']),
    phone: nullableString(record['phone']),
    descripcion: nullableString(record['descripcion']),
    image: normalizeImage(record['image']),
    status,
    createdAt: optionalString(record['createdAt']),
    updatedAt: optionalString(record['updatedAt']),
  };
}

function normalizeImage(raw: unknown): UserImageDto | null {
  const record = asRecord(raw);
  const uuid = stringField(record, ['uuid']);
  const name = stringField(record, ['name']);
  const contentType = stringField(record, ['contentType']);
  const size = stringField(record, ['size']);
  const url = stringField(record, ['url']);
  if (!record || !uuid || !name || !contentType || !size || !url) return null;
  const order = numberField(record, ['order']);
  return { uuid, name, contentType, order, size, url };
}

function normalizeStatus(raw: unknown): UserStatusDto | null {
  const record = asRecord(raw);
  const name = stringField(record, ['name']);
  const value = stringField(record, ['value', 'uuid']);
  const tone = stringField(record, ['tone']);
  return name && value && tone ? { name, value, tone } : null;
}

function normalizeRole(raw: unknown): UserRoleOption | null {
  const record = asRecord(raw);
  const uuid = stringField(record, ['uuid']);
  const code = stringField(record, ['code']);
  const name = stringField(record, ['name']);
  return uuid && code && name ? { uuid, code, name } : null;
}

function normalizeNamed(raw: unknown): UserRoleOption | null {
  const record = asRecord(raw);
  const uuid = stringField(record, ['uuid', 'value']);
  const name = stringField(record, ['name']);
  return uuid && name ? { uuid, code: uuid, name } : null;
}

function normalizeAccessRole(raw: unknown): UserAccessDto['role'] {
  const record = asRecord(raw);
  const uuid = stringField(record, ['uuid']);
  const code = stringField(record, ['code']);
  const name = stringField(record, ['name']);
  return uuid && code && name ? { uuid, code, name } : null;
}

function normalizeSystem(raw: unknown): UserAccessDto['systems'][number] | null {
  const record = asRecord(raw);
  const uuid = stringField(record, ['uuid']);
  const code = stringField(record, ['code']);
  const name = stringField(record, ['name']);
  const displayOrder = numberField(record, ['displayOrder']);
  return uuid && code && name && displayOrder !== null
    ? { uuid, code, name, displayOrder }
    : null;
}

function toDownload(
  response: HttpResponse<Blob>,
  fallbackFilename: string,
): UserApiDownload {
  if (!response.body) throw new SettingsApiError('unexpected', 0);
  return {
    blob: response.body,
    filename:
      contentDispositionFilename(response.headers.get('Content-Disposition')) ??
      fallbackFilename,
  };
}

function contentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.replace(/^"|"$/g, ''));
    } catch {
      return encoded.replace(/^"|"$/g, '');
    }
  }
  return header.match(/filename="?([^";]+)"?/i)?.[1] ?? null;
}

function detailMessages(details: unknown): string[] {
  const record = asRecord(details);
  if (!record) return [];
  const messages: string[] = [];
  for (const value of Object.values(record)) {
    if (typeof value === 'string' && value.trim()) messages.push(value.trim());
    if (!Array.isArray(value)) continue;
    for (const item of value) {
      if (typeof item === 'string' && item.trim()) messages.push(item.trim());
    }
  }
  return messages;
}

function requiredData(body: unknown): unknown {
  const data = unwrapData(body);
  if (data === undefined || data === null) {
    throw new SettingsApiError('unexpected', 0);
  }
  return data;
}

function unwrapData(body: unknown): unknown {
  const record = asRecord(body);
  if (
    record &&
    'data' in record &&
    (Object.keys(record).length === 1 ||
      'message' in record ||
      'status' in record ||
      'error' in record)
  ) {
    return record['data'];
  }
  if (
    record &&
    !('data' in record) &&
    ('message' in record || 'status' in record || 'error' in record)
  ) {
    return undefined;
  }
  return body;
}

function firstArray(record: Record<string, unknown>, keys: string[]): unknown[] {
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringField(
  record: Record<string, unknown> | null,
  keys: string[],
): string | null {
  if (!record) return null;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function numberField(
  record: Record<string, unknown> | null,
  keys: string[],
): number | null {
  if (!record) return null;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
}
